import { FloorplanData } from './types';

/**
 * Floorplan color constants - leveraging Tailwind colors where available
 */
export const FLOORPLAN_COLORS = {
  // Room colors
  ROOM_BACKGROUND: '#F1ECDC', // From tailwind.config.js (brand.secondary)
  
  // Corridor colors (light green)
  CORRIDOR_FILL: '#c8e6c9', // Light green fill
  CORRIDOR_STROKE: '#a5d6a7', // Medium green stroke
  
  // Background floor colors
  FLOOR_BACKGROUND: '#f5f5f5', // Light gray background
  FLOOR_STROKE: '#e0e0e0', // Medium gray stroke
  
  // Facility colors
  Reception_FILL: '#e3f2fd', // Light blue fill
  Reception_STROKE: '#90caf9', // Medium blue stroke
  STAIRCASE_FILL: '#fafafa', // Very light gray fill
  STAIRCASE_STROKE: '#bdbdbd', // Medium gray stroke
} as const;

/**
 * Floorplan label constants for easier management
 */
export const FLOORPLAN_LABELS = {
  // Room names
  VARADA: 'VARADA',
  SHARAVATHI: 'SHARAVATHI',
  BHADRA: 'BHADRA',
  BHIMA: 'BHIMA',
  KRISHNA: 'KRISHNA',
  SANGAMA: 'SANGAMA',
  TUNGA: 'TUNGA',
  CAUVERY: 'CAUVERY',
  
  // Open desk labels
  SEATER_2: '2 Seater',
  SEATER_3: '3 Seater',
  SEATER_4: '4 Seater',
  
  // Facility labels
  CENTRAL_CORRIDOR: 'Corridor',
  Reception: 'Reception',
  STAIRCASE: 'Staircase',
} as const;

/**
 * Data-driven floorplan configuration matching the actual co-working space layout
 * Based on the architectural floorplan with rooms, open desks, corridor, Receptions, and staircase
 */
export const floorplanData: FloorplanData = {
  viewBox: {
    width: 679, // 905 * 0.75
    height: 670, // 905 * 0.75
  },
  elements: [
    // Background floor
    {
      type: 'room',
      x: 0,
      y: 0,
      width: 679, // 905 * 0.75
      height: 670, // Match viewBox height
      fill: FLOORPLAN_COLORS.FLOOR_BACKGROUND,
      stroke: FLOORPLAN_COLORS.FLOOR_STROKE,
    },

    // Central corridor (green background)
    {
      type: 'room',
      x: 263, // 350 * 0.75
      y: 143, // 190 * 0.75
      width: 420, // 560 * 0.75
      height: 45, // 60 * 0.75
      fill: FLOORPLAN_COLORS.CORRIDOR_FILL,
      stroke: FLOORPLAN_COLORS.CORRIDOR_STROKE,
      label: FLOORPLAN_LABELS.CENTRAL_CORRIDOR,
      labelX: 473, // 630 * 0.75
      labelY: 165, // 220 * 0.75
    },
    {
      type: 'room',
      x: 188, // 250 * 0.75
      y: 143, // 190 * 0.75
      width: 75, // 100 * 0.75
      height: 131, // 175 * 0.75
      fill: FLOORPLAN_COLORS.CORRIDOR_FILL,
      stroke: FLOORPLAN_COLORS.CORRIDOR_STROKE,
    },
    {
      type: 'room',
      x: 135, // 180 * 0.75
      y: 274, // 365 * 0.75
      width: 128, // 170 * 0.75
      height: 416, // 555 * 0.75
      fill: FLOORPLAN_COLORS.CORRIDOR_FILL,
      stroke: FLOORPLAN_COLORS.CORRIDOR_STROKE,
    },
    {
      type: 'room',
      x: 263, // 350 * 0.75
      y: 480, // 640 * 0.75
      width: 420, // 560 * 0.75
      height: 45, // 60 * 0.75
      fill: FLOORPLAN_COLORS.CORRIDOR_FILL,
      stroke: FLOORPLAN_COLORS.CORRIDOR_STROKE,
      label: FLOORPLAN_LABELS.CENTRAL_CORRIDOR,
      labelX: 473, // 630 * 0.75
      labelY: 503, // 670 * 0.75
    },

    // Top row of rooms (above corridor)
    
    // VARADAP - 10 seater, brown (at left wall edge) - open desk area
    {
      type: 'room',
      x: 0,
      y: 0,
      width: 188, // 250 * 0.75
      height: 273, // 364 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.VARADA,
      labelX: 94, // 125 * 0.75
      labelY: 15, // 20 * 0.75
    },
    
    // 3-seater room (left of SHARAVATHI)
    {
      type: 'room',
      x: 188, // 250 * 0.75
      y: 0,
      width: 90, // 120 * 0.75
      height: 143, // 190 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.SEATER_3,
      labelX: 233, // 310 * 0.75
      labelY: 15, // 20 * 0.75
    },
    
    // SHARAVATHI - 6 seater, light yellow (open desk area)
    {
      type: 'room',
      x: 278, // 370 * 0.75
      y: 0,
      width: 150, // 200 * 0.75
      height: 143, // 190 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.SHARAVATHI,
      labelX: 353, // 470 * 0.75
      labelY: 15, // 20 * 0.75
    },
    
    // BHADRA - 6 seater, light yellow (open desk area)
    {
      type: 'room',
      x: 428, // 570 * 0.75
      y: 0,
      width: 150, // 200 * 0.75
      height: 143, // 190 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.BHADRA,
      labelX: 503, // 670 * 0.75
      labelY: 15, // 20 * 0.75
    },
    
    // 3-seater room (rightmost)
    {
      type: 'room',
      x: 578, // 770 * 0.75
      y: 0,
      width: 113, // 150 * 0.75
      height: 143, // 190 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.SEATER_3,
      labelX: 620, // 845 * 0.75
      labelY: 15, // 20 * 0.75
    },

    // Middle row (below corridor)
    
    // 2-seater room (above meeting room)
    {
      type: 'room',
      x: 263, // 350 * 0.75
      y: 188, // 250 * 0.75
      width: 150, // 200 * 0.75
      height: 75, // 100 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.SEATER_2,
      labelX: 338, // 450 * 0.75
      labelY: 200, // Aligned with BHIMA and 4 Seater
    },
    
    // KRISHNA - 4 seater, light brown (top of stack, at wall edge) - open desk area - beside 2-seater
    {
      type: 'room',
      x: 413, // 550 * 0.75
      y: 188, // 250 * 0.75
      width: 165, // 220 * 0.75
      height: 150, // 200 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.KRISHNA,
      labelX: 495, // 660 * 0.75
      labelY: 200, // Aligned with 2 Seater and 4 Seater
    },
    
    // BHIMA - 4 seater, light brown (below KRISHNA, stacked, at wall edge) - open desk area - beside SANGAMA
    {
      type: 'room',
      x: 413, // 550 * 0.75
      y: 338, // 450 * 0.75
      width: 165, // 220 * 0.75
      height: 143, // 190 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.BHIMA,
      labelX: 495, // 660 * 0.75
      labelY: 353, // 470 * 0.75
    },

    // 4 Seater - shares wall with BHIMA and KRISHNA (extends to right wall edge)
    {
      type: 'room',
      x: 578, // 770 * 0.75
      y: 188, // 250 * 0.75
      width: 150, // 200 * 0.75
      height: 293, // 390 * 0.75
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.SEATER_4,
      labelX: 635, // 870 * 0.75 - centered horizontally
      labelY: 200, // Higher position
    },

    // Bottom row

    {
      type: 'room',
      x: 263, // 350 * 0.75
      y: 525, // 700 * 0.75
      width: 263, // 350 * 0.75
      height: 158, // Reduced by 30 from 188
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.CAUVERY,
      labelX: 394, // 525 * 0.75
      labelY: 540, // 720 * 0.75
    },
    
    // TUNGA - 6 seater, light yellow (open desk area)
    {
      type: 'room',
      x: 525, // 700 * 0.75
      y: 525, // 700 * 0.75
      width: 165, // 220 * 0.75
      height: 158, // Reduced by 30 from 188
      fill: FLOORPLAN_COLORS.ROOM_BACKGROUND,
      label: FLOORPLAN_LABELS.TUNGA,
      labelX: 608, // 810 * 0.75
      labelY: 540, // 720 * 0.75
    },

    // Reception area (moved next to VARADAP, below it)
    {
      type: 'room',
      x: 0,
      y: 270, // 360 * 0.75
      width: 135, // 180 * 0.75
      height: 210, // 280 * 0.75
      fill: FLOORPLAN_COLORS.Reception_FILL,
      stroke: FLOORPLAN_COLORS.Reception_STROKE,
      label: FLOORPLAN_LABELS.Reception,
      labelX: 68, // 90 * 0.75
      labelY: 285, // 380 * 0.75
    },

     // Staircase (below Reception)
      {
        type: 'room',
        x: 0,
        y: 480, // 640 * 0.75
        width: 135, // 180 * 0.75
        height: 210, // 280 * 0.75
        fill: FLOORPLAN_COLORS.STAIRCASE_FILL,
        stroke: FLOORPLAN_COLORS.STAIRCASE_STROKE,
        label: FLOORPLAN_LABELS.STAIRCASE,
        labelX: 68, // 90 * 0.75
        labelY: 495, // 660 * 0.75
      },    
  ],
};
